import { IUserWorking } from 'store/modules/goal/types'
import { IStore } from 'store/modules/store/types'

import { BulkGoalProfile, StoreGoalConfig } from '../types'
import { deriveMonthGoals } from '../utils'
import { buildGoalSections } from './dateUtils'

export interface SubmittedMonthGoal {
  bonus: number
  bonusPercentage: boolean
  commission: number
  name: string
  type: 'store' | 'user'
  typeBonus: 'store' | 'user'
  value: number
}

export interface SubmittedUserWorking {
  active: boolean
  dsr: boolean
  goalWeight: number
  userId: string
  weightLocked: boolean
}

export interface SubmittedGoalConfig {
  distribute: boolean
  dsr: number
  fixedCommission: number
  mainGoals: { price: number; ticket: number; total: number }
  monthGoals: SubmittedMonthGoal[]
  salary: { base: number; complement: boolean; directCommission: boolean; splitCommission: boolean }
  sections: StoreGoalConfig['sections']
  storeSold: boolean
  usersWorking: SubmittedUserWorking[]
}

export interface BuildSubmittedGoalConfigOptions {
  month: string
  store: Pick<IStore, '_id' | 'users'>
}

export const normalizeMonthGoal = (goal: BulkGoalProfile): SubmittedMonthGoal => ({
  bonus: goal.bonusPercentage === 'no-bonus' && !goal.bonus ? 0 : goal.bonus,
  bonusPercentage: goal.bonusPercentage === 'no-bonus' ? false : goal.bonusPercentage,
  commission: goal.commission,
  name: goal.name,
  type: goal.type,
  typeBonus: goal.typeBonus,
  value: Number(goal.value.toFixed(2))
})

const getUserId = (user: IUserWorking) =>
  typeof user.userId === 'object' ? user.userId._id : user.userId

const normalizeSubmittedUser = (user: IUserWorking, dsr: number): SubmittedUserWorking => ({
  active: Boolean(user.active),
  dsr: user.dsr ?? dsr > 0,
  goalWeight: user.goalWeight ?? 1,
  userId: getUserId(user),
  weightLocked: Boolean(user.weightLocked)
})

const getStoreSellerIds = (store: BuildSubmittedGoalConfigOptions['store']) => new Set(
  store.users
    .filter(user => user.stores.find(userStore => userStore.storeId === store._id)?.type === 'seller')
    .map(user => user._id)
)

const buildSubmittedUsers = (
  config: StoreGoalConfig,
  store: BuildSubmittedGoalConfigOptions['store'],
  dsr: number
) => {
  const storeSellerIds = getStoreSellerIds(store)
  const configuredUsers = [...config.usersWorking, ...config.usersNotWorking]
    .filter(user => storeSellerIds.has(getUserId(user)))
    .map(user => normalizeSubmittedUser(user, dsr))
  const users = configuredUsers.length > 0
    ? configuredUsers
    : Array.from(storeSellerIds).map(userId => ({
        active: true,
        dsr: dsr > 0,
        goalWeight: 1,
        userId,
        weightLocked: false
      }))

  return users.sort((first, second) => first.userId.localeCompare(second.userId))
}

export const buildSubmittedGoalConfig = (
  config: StoreGoalConfig,
  { month, store }: BuildSubmittedGoalConfigOptions
): SubmittedGoalConfig => {
  const directCommission = Boolean(config.directCommission)
  const dsr = Number(config.dsr || 0)
  const periodType = config.periodType || 'single'
  const usersWorking = buildSubmittedUsers(config, store, dsr)
  const monthGoals = deriveMonthGoals(
    config.monthGoalTemplates,
    config.total,
    usersWorking.filter(user => user.active).length
  )

  return {
    distribute: Boolean(config.distribute),
    dsr,
    fixedCommission: config.hasFixedCommission ? Number(config.fixedCommission || 0) / 100 : 0,
    mainGoals: {
      price: Number(config.price || 0),
      ticket: Number(config.ticket || 0),
      total: Number(config.total || 0)
    },
    monthGoals: monthGoals.map(normalizeMonthGoal),
    salary: {
      base: Number(config.salaryBase || 0),
      complement: Boolean(directCommission && config.inheritedSalaryComplement),
      directCommission,
      splitCommission: Boolean(config.splitCommission)
    },
    sections: config.sections.length > 0 ? config.sections : buildGoalSections(month, periodType),
    storeSold: Boolean(config.storeSold),
    usersWorking
  }
}
